import { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../config';
import { requestErrorDetails } from './requestError.mjs';

export default function useMedicineReview({ workspaceId, generationId, language, enabled }) {
  const key = JSON.stringify([Boolean(enabled), workspaceId, generationId, language]);
  const [state, setState] = useState({ key, data: null, loading: Boolean(enabled), error: '' }), [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    if (!enabled) { setState({ key, data: null, loading: false, error: '' }); return; }
    let live = true, timer, controller;
    async function load() {
      controller = new AbortController();
      try {
        const { data } = await axiosInstance.get(`/api/seo/workspaces/${workspaceId}/review`, { params: { generation: generationId, language }, signal: controller.signal, timeout: 15000 });
        if (!live) return;
        if (generationId && data.generationId && data.generationId !== generationId) throw new Error('This generation has been replaced. Open the current keyword workspace to review its latest draft.');
        setState({ key, data, loading: false, error: '' });
        if (!data.ready && !['failed', 'uncertain', 'cancelled'].includes(data.status)) timer = setTimeout(load, 3000);
      } catch (failure) { if (live && !controller.signal.aborted) setState(previous => ({ ...previous, loading: false, error: requestErrorDetails(failure, { readOnly: true, fallback: 'Medicine review could not be loaded. Refresh its saved progress before starting more work.' }).message })); }
    }
    // A background refresh after Save keeps the current form and success receipt
    // mounted. A new identity or generation must never reuse the old seed.
    setState(previous => previous.key === key && previous.data?.ready ? { ...previous, error: '' } : { key, data: null, loading: true, error: '' }); load();
    return () => { live = false; clearTimeout(timer); controller?.abort(); };
  }, [enabled, generationId, key, language, revision, workspaceId]);
  return { ...(state.key === key ? state : { data: null, loading: Boolean(enabled), error: '' }), refresh };
}
